import { jaText } from '../i18n';
import {
  AFTER_CRAWL_COMPLETE_CHOICES,
  AFTER_SCOPE_CRAWL_CHOICES,
  AFTER_SUCCESS_CHOICES,
  CONFIRM_REGISTER_CHOICE,
  CRAWL_INPUT_CHOICES,
  INGEST_PATH_CHOICES,
  PAGES_INPUT_CHOICES,
  PREVIEW_SCOPE_CHOICES,
  REDO_CHOICE,
} from './agentIngest';

export type GuideChoiceSource = 'model' | 'crawl-preview' | 'ingest-wizard' | 'after-success' | 'fixed';

const PAGE_IMPORT_CHOICES = [jaText('Import this content'), REDO_CHOICE];

function sameChoices(actual: string[], expected: readonly string[]): boolean {
  if (actual.length === 0 || actual.length !== expected.length) return false;
  return actual.every((item, index) => item === expected[index]);
}

/**
 * Classifies the buttons left on screen. Canonical sets win over the model,
 * because the home replaces those chips after the callable returns.
 */
export function inferGuideSource(args: {
  choices: string[] | undefined;
  fromModel: boolean;
}): GuideChoiceSource {
  const choices = (args.choices ?? []).map((label) => label.trim()).filter(Boolean);
  if (sameChoices(choices, PREVIEW_SCOPE_CHOICES) || sameChoices(choices, [CONFIRM_REGISTER_CHOICE, REDO_CHOICE])) {
    return 'crawl-preview';
  }
  if (
    sameChoices(choices, AFTER_SUCCESS_CHOICES) ||
    sameChoices(choices, AFTER_SCOPE_CRAWL_CHOICES) ||
    sameChoices(choices, AFTER_CRAWL_COMPLETE_CHOICES)
  ) {
    return 'after-success';
  }
  if (
    sameChoices(choices, INGEST_PATH_CHOICES) ||
    sameChoices(choices, CRAWL_INPUT_CHOICES) ||
    sameChoices(choices, PAGES_INPUT_CHOICES)
  ) {
    return 'ingest-wizard';
  }
  if (sameChoices(choices, PAGE_IMPORT_CHOICES)) return 'fixed';
  return args.fromModel ? 'model' : 'fixed';
}

export interface GuideTurnMessage {
  id: string;
  role: string;
  content: string;
  choices?: string[];
  logId?: string;
  actionType?: string;
  executedAction?: {
    label: string;
    success: boolean;
    running?: boolean;
  };
}

export interface GuidePresentation {
  messageId: string;
  logId?: string;
  userMessage: string;
  presentedReply: string;
  choices?: string[];
  choiceSource: GuideChoiceSource;
  actionType?: string;
  actionLabel?: string;
  actionSuccess: boolean | null;
  actionPending: boolean;
  conversationLength: number;
}

/** The last assistant turn, paired with the user message it answers. */
export function guidePresentation(messages: GuideTurnMessage[]): GuidePresentation | null {
  let lastIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') {
      lastIndex = i;
      break;
    }
  }
  if (lastIndex < 0) return null;

  const last = messages[lastIndex];
  let userMessage = '';
  for (let i = lastIndex - 1; i >= 0; i--) {
    if (messages[i].role === 'user') {
      userMessage = messages[i].content;
      break;
    }
  }

  const pending = last.executedAction?.running === true;
  const logId = typeof last.logId === 'string' && last.logId.trim() ? last.logId.trim() : undefined;
  return {
    messageId: last.id,
    ...(logId ? { logId } : {}),
    userMessage,
    presentedReply: last.content,
    ...(last.choices && last.choices.length > 0 ? { choices: last.choices } : {}),
    choiceSource: inferGuideSource({ choices: last.choices, fromModel: Boolean(logId) }),
    ...(last.actionType ? { actionType: last.actionType } : {}),
    ...(last.executedAction ? { actionLabel: last.executedAction.label } : {}),
    actionSuccess: !last.executedAction || pending ? null : last.executedAction.success,
    actionPending: pending,
    conversationLength: messages.length,
  };
}
